<script setup lang="ts">
  import { useGetAccountStates } from "@/api/account-state/account-state.ts"
  import { useGetAccount } from "@/api/account/account.ts"
  import { useGetAlertsAndBanners } from "@/api/alerts/alerts.ts"
  import { useGetFeatureFlags } from "@/api/config/config.ts"
  import {
    AlertSeverity,
    AlertTag,
    BannerTag,
    FlexMarketSubscriptionState,
    MoveWithUsChangeOfTenancyStatus,
    SmartChargingState,
    SmartPayAsYouGoState,
  } from "@/api/model"
  import nativeHelper from "@/lib/nativeHelper.ts"
  import { useGetAccountId } from "@/lib/useGetAccountId"
  import { cn } from "@/lib/utils"
  import { computed } from "vue"
  import AccountClosedAlert from "@/components/shared/AccountClosedAlert.vue"
  import AlertList from "@/components/shared/AlertList.vue"
  import { usePageLevelMaintenance } from "@/components/shared/maintenance-banner/usePageLevelMaintenance"
  import SwitchProgressCard from "@/components/ui/switch-progress/SwitchProgressCard.vue"

  import BalanceCard from "@/views/myaccount/home/components/balance/BalanceCard.vue"
  import DirectDebitCard from "@/views/myaccount/home/components/direct-debit/DirectDebitCard.vue"
  import MarketingBanners from "@/views/myaccount/home/components/MarketingBanners.vue"
  import MeterReadingsCard from "@/views/myaccount/home/components/meter-readings/MeterReadingsCard.vue"
  import MoveOutScheduledAlert from "@/views/myaccount/home/components/move-out-timeline/MoveOutScheduledAlert.vue"
  import MoveOutTimelineCard from "@/views/myaccount/home/components/move-out-timeline/MoveOutTimelineCard.vue"
  import { useClosureSequence } from "@/views/myaccount/home/components/move-out-timeline/useClosureSequence"
  import { useMoveOutScheduled } from "@/views/myaccount/home/components/move-out-timeline/useMoveOutScheduled"
  import { useMoveOutTimeline } from "@/views/myaccount/home/components/move-out-timeline/useMoveOutTimeline"
  import MeterReadFrequencyModal from "@/views/myaccount/meter-read-frequency/components/MeterReadFrequencyModal.vue"
  import MoveWithUsRecordAlert from "@/views/myaccount/moving-home/MoveWithUsRecordAlert.vue"
  import { isMoveWithUsInFlight } from "@/views/myaccount/moving-home/movingHomeState"
  import { useMoveWithUsRecord } from "@/views/myaccount/moving-home/useMoveWithUsRecord"

  import EnergyUsageCard from "./components/energy-usage/EnergyUsageCard.vue"
  import FlexMarketSubscriptionCard from "./components/flex-market-subscription/FlexMarketSubscriptionCard.vue"
  import SmartChargingCard from "./components/smart-charging/SmartChargingCard.vue"

  const { accountId } = useGetAccountId()

  const account = useGetAccount(accountId)
  const customerQuery = useGetAccountId()
  const accountStatesQuery = useGetAccountStates(customerQuery.accountId)
  const getAlertsQuery = useGetAlertsAndBanners(accountId, {
    alertTag: AlertTag.home,
  })
  const getBannersQuery = useGetAlertsAndBanners(accountId, {
    bannerTag: BannerTag.home,
  })

  const { phase: closurePhase, isLoading: isClosureLoading } =
    useClosureSequence()
  const isPlayingClosure = computed(() => !!closurePhase.value)
  const showCards = computed(() => closurePhase.value !== "closed")

  const featureFlagsQuery = useGetFeatureFlags()
  const { isUnderMaintenance } = usePageLevelMaintenance()
  const { isMoveOutScheduled } = useMoveOutScheduled()
  const { isMovingOut } = useMoveOutTimeline()
  const { record: moveWithUsRecord } = useMoveWithUsRecord()

  const showSwitchProgress =
    !nativeHelper.isNative() && !nativeHelper.isInAppBrowser()

  const isStandardAccount = computed(
    () =>
      accountStatesQuery.data.value?.smartPayAsYouGo !==
      SmartPayAsYouGoState.on,
  )

  const hasActiveFlexMarketSubscription = computed(
    () =>
      featureFlagsQuery.data.value?.showSoChargedWeb === true &&
      accountStatesQuery.data.value?.flexMarketSubscription ===
        FlexMarketSubscriptionState.active,
  )

  const isEnrolledInSmartCharging = computed(
    () =>
      accountStatesQuery.data.value?.smartCharging ===
      SmartChargingState.enrolled,
  )

  const hasCriticalAlert = computed(() =>
    getAlertsQuery.data.value?.alerts?.some(
      (alert) => alert.severity === AlertSeverity.critical,
    ),
  )

  const hasMarketingBanner = computed(() => {
    return Boolean(getBannersQuery.data.value?.marketingBanner)
  })

  const hasAlerts = computed(() =>
    Boolean(getAlertsQuery.data.value?.alerts?.length),
  )

  // From the LDOR the timeline card replaces the green alerts. A failed move
  // still needs the customer to act, so it stays.
  const inFlightRecord = computed(() => {
    const record = moveWithUsRecord.value
    if (!isMoveWithUsInFlight(record)) return undefined
    if (
      isMovingOut.value &&
      record?.status !== MoveWithUsChangeOfTenancyStatus.FAILED
    ) {
      return undefined
    }
    return record
  })
</script>

<template>
  <template v-if="account?.data?.value?.isClosed && !isPlayingClosure">
    <!-- Wait for the timeline so the alert doesn't flash before the closure
    sequence takes over. -->
    <AccountClosedAlert v-if="!isClosureLoading" />
  </template>
  <div v-else class="grid grid-cols-1 gap-4 sm:gap-6">
    <template v-if="!isPlayingClosure">
      <MarketingBanners
        v-if="hasMarketingBanner"
        :class="cn({ 'order-last': isUnderMaintenance || hasCriticalAlert })"
      />
      <AlertList v-if="hasAlerts" :truncate-alerts="true" />
      <MoveWithUsRecordAlert v-if="inFlightRecord" :record="inFlightRecord" />
      <MoveOutScheduledAlert v-if="isMoveOutScheduled && !isMovingOut" />
      <SwitchProgressCard v-if="showSwitchProgress" />
    </template>
    <Transition
      mode="out-in"
      leave-active-class="transition duration-300 ease-in motion-reduce:transition-none"
      leave-to-class="-translate-y-2 opacity-0"
      enter-active-class="transition duration-300 ease-out motion-reduce:transition-none"
      enter-from-class="-translate-y-4 opacity-0"
    >
      <AccountClosedAlert v-if="closurePhase === 'closed'" role="status" />
      <MoveOutTimelineCard
        v-else
        :hold-closure="closurePhase === 'pending'"
        :animate-closure="closurePhase === 'closing'"
      />
    </Transition>
    <TransitionGroup
      tag="div"
      class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6"
      data-testid="cards-container"
      leave-active-class="transition-opacity duration-300 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0"
    >
      <template v-if="showCards">
        <template v-if="isStandardAccount">
          <FlexMarketSubscriptionCard
            v-if="hasActiveFlexMarketSubscription"
            key="flex-market-subscription"
          />
          <BalanceCard key="balance" />
          <DirectDebitCard key="direct-debit" />
          <SmartChargingCard
            v-if="isEnrolledInSmartCharging"
            key="smart-charging"
          />
        </template>
        <MeterReadingsCard key="meter-readings" />
        <EnergyUsageCard key="energy-usage" />
      </template>
    </TransitionGroup>
  </div>
  <MeterReadFrequencyModal />
</template>
